import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

// Utility: create cropped image from canvas (downscaled — mobile browsers fail toBlob on huge canvases)
async function getCroppedImg(imageSrc, pixelCrop, outputType = 'image/jpeg') {
  const image = await createImage(imageSrc);
  const MAX_DIM = 1600;
  let scale = 1;
  if (Math.max(pixelCrop.width, pixelCrop.height) > MAX_DIM) {
    scale = MAX_DIM / Math.max(pixelCrop.width, pixelCrop.height);
  }
  const toBlob = (canvas) => new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), outputType, 0.92);
  });

  // First attempt
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(pixelCrop.width * scale);
  canvas.height = Math.round(pixelCrop.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, canvas.width, canvas.height);
  let blob = await toBlob(canvas);

  // Mobile fallback: toBlob can return null under memory pressure — retry smaller
  if (!blob && scale > 0.25) {
    scale = 0.25;
    canvas.width = Math.round(pixelCrop.width * scale);
    canvas.height = Math.round(pixelCrop.height * scale);
    canvas.getContext('2d').drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, canvas.width, canvas.height);
    blob = await toBlob(canvas);
  }
  if (!blob) throw new Error('Could not process image — please try a smaller photo');
  const name = outputType === 'image/jpeg' ? 'image.jpg' : 'image.png';
  return new File([blob], name, { type: outputType });
}

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
}

export default function ImageCropper({ imageSrc, onCropDone, onCancel, aspect = 16 / 9, shape = 'rect', title = 'Crop Image' }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const [cropError, setCropError] = useState('');

  const handleDone = async () => {
    let area = croppedAreaPixels;
    // Fast taps can beat react-easy-crop's first crop computation — retry briefly
    if (!area) {
      for (let i = 0; i < 5 && !area; i++) {
        await new Promise(r => setTimeout(r, 150));
        area = croppedAreaPixels;
      }
    }
    if (!area) { setCropError('Crop area not ready — please move the image slightly and try again'); return; }
    setProcessing(true);
    setCropError('');
    try {
      const croppedFile = await getCroppedImg(imageSrc, area);
      onCropDone(croppedFile);
    } catch (e) {
      console.error('Crop failed:', e);
      setCropError(e.message || 'Could not process image — try a smaller photo');
    }
    setProcessing(false);
  };

  // Aspect ratio presets
  const aspects = [
    { label: 'Banner (16:9)', value: 16 / 9 },
    { label: 'Square (1:1)', value: 1 },
    { label: 'Portrait (3:4)', value: 3 / 4 },
    { label: 'Wide (21:9)', value: 21 / 9 },
    { label: 'Free', value: null },
  ];
  const [activeAspect, setActiveAspect] = useState(aspect);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <h3 style={{ color: 'white', fontSize: '18px', fontWeight: 600, margin: 0 }}>{title}</h3>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '28px', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
      </div>

      {/* Cropper area */}
      <div style={{ flex: 1, position: 'relative' }}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={activeAspect || undefined}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          cropShape={shape}
          showGrid={true}
          style={{
            containerStyle: { background: '#111' },
            cropAreaStyle: { border: '2px solid #C9A84C', color: 'rgba(0,0,0,0.6)' },
          }}
        />
      </div>

      {/* Controls */}
      <div style={{ padding: '16px 24px', background: 'rgba(0,0,0,0.95)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        {/* Aspect ratio selector */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {aspects.map(a => (
            <button key={a.label} onClick={() => setActiveAspect(a.value)} style={{
              padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
              border: `1px solid ${activeAspect === a.value ? '#C9A84C' : 'rgba(255,255,255,0.15)'}`,
              background: activeAspect === a.value ? '#C9A84C22' : 'transparent',
              color: activeAspect === a.value ? '#C9A84C' : 'rgba(255,255,255,0.6)',
              transition: 'all 0.2s',
            }}>{a.label}</button>
          ))}
        </div>

        {/* Zoom slider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', justifyContent: 'center' }}>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', minWidth: '40px' }}>Zoom</span>
          <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={e => setZoom(Number(e.target.value))}
            style={{ flex: 1, maxWidth: '300px', accentColor: '#C9A84C' }} />
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', minWidth: '40px' }}>{Math.round(zoom * 100)}%</span>
        </div>

        {/* Action buttons */}
        {cropError && <p style={{ color: '#ff8a9e', fontSize: '13px', textAlign: 'center', margin: '0 0 10px' }}>{cropError}</p>}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button onClick={onCancel} style={{
            padding: '12px 32px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.2)',
            background: 'transparent', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={handleDone} disabled={processing} style={{
            padding: '12px 40px', borderRadius: '10px', border: 'none',
            background: 'linear-gradient(135deg, #C9A84C, #B8960F)', color: '#0a0a14',
            fontSize: '14px', fontWeight: 700, cursor: processing ? 'not-allowed' : 'pointer',
            opacity: processing ? 0.6 : 1, letterSpacing: '0.3px',
          }}>{processing ? 'Processing...' : 'Apply Crop'}</button>
        </div>
      </div>
    </div>
  );
}
